const CSRF_COOKIE_NAME = 'cao_csrf'
const CSRF_HEADER_NAME = 'x-csrf-token'
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])
const INSTALLED_FLAG = '__caoCsrfFetchInstalled'

type CsrfWindow = Window & { [INSTALLED_FLAG]?: boolean }

let mintingCookie: Promise<string | null> | null = null

function readCookie(name: string): string | null {
  const parts = document.cookie ? document.cookie.split(';') : []
  const prefix = `${name}=`
  for (const part of parts) {
    const trimmed = part.trim()
    if (!trimmed.startsWith(prefix)) { continue }
    const raw = trimmed.slice(prefix.length)
    try {
      return decodeURIComponent(raw)
    } catch {
      return null
    }
  }
  return null
}

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') { return input }
  if (input instanceof URL) { return input.href }
  return input.url
}

function isSameOrigin(input: RequestInfo | URL): boolean {
  try {
    const parsed = new URL(resolveUrl(input), window.location.origin)
    return parsed.origin === window.location.origin
  } catch {
    return false
  }
}

function resolveMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) { return init.method.toUpperCase() }
  if (input instanceof Request) { return input.method.toUpperCase() }
  return 'GET'
}

function withCsrfHeader(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  token: string,
): { input: RequestInfo | URL; init?: RequestInit } {
  if (input instanceof Request) {
    const headers = new Headers(input.headers)
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => {
        headers.set(key, value)
      })
    }
    headers.set(CSRF_HEADER_NAME, token)
    return { input: new Request(input, { ...init, headers }) }
  }

  const headers = new Headers(init?.headers)
  headers.set(CSRF_HEADER_NAME, token)
  return { input, init: { ...init, headers } }
}

function mintCsrfCookie(originalFetch: typeof fetch): Promise<string | null> {
  const existing = readCookie(CSRF_COOKIE_NAME)
  if (existing) { return Promise.resolve(existing) }
  if (!mintingCookie) {
    mintingCookie = originalFetch('/api/health', {
      credentials: 'include',
      cache: 'no-store',
    })
      .then(() => readCookie(CSRF_COOKIE_NAME))
      .finally(() => {
        mintingCookie = null
      })
  }
  return mintingCookie
}

export function installCsrfFetch(): void {
  const target = window as CsrfWindow
  if (target[INSTALLED_FLAG]) { return }
  target[INSTALLED_FLAG] = true

  const originalFetch = window.fetch.bind(window)
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = resolveMethod(input, init)
    if (!UNSAFE_METHODS.has(method) || !isSameOrigin(input)) {
      return originalFetch(input, init)
    }

    const token = readCookie(CSRF_COOKIE_NAME) ?? await mintCsrfCookie(originalFetch)
    if (!token) {
      return originalFetch(input, init)
    }

    const signed = withCsrfHeader(input, init, token)
    return originalFetch(signed.input, signed.init)
  }
}
